// File: src/services/api.js
// Thin fetch wrapper for the REST API.
//
// The existing components each call fetch directly and hand-attach the auth
// header; new code should go through here instead. Deliberately not a migration
// of those call sites - that is a separate change.

import { API_BASE_URL } from '../config';
import { handleUnauthorized } from '../utils/session';

// Thrown for any non-2xx response so callers can use try/catch rather than
// checking response.ok everywhere.
export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

const request = async (method, path, { token, body, signal } = {}) => {
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    signal,
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  // Expired session: notify once and log out. Callers get an ApiError they can
  // simply not handle, since the app is already navigating away.
  if (handleUnauthorized(response)) {
    throw new ApiError('Session expired', 401, null);
  }

  // 204 and empty bodies are valid; don't blow up trying to parse them.
  const text = await response.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    const message = (payload && payload.error) || `Request failed (${response.status})`;
    throw new ApiError(message, response.status, payload);
  }

  return payload;
};

export const apiGet = (path, options) => request('GET', path, options);
export const apiPost = (path, body, options) => request('POST', path, { ...options, body });
export const apiPut = (path, body, options) => request('PUT', path, { ...options, body });
export const apiDelete = (path, options) => request('DELETE', path, options);

// Downloads a file endpoint and hands it to the browser. Mirrors the blob
// pattern already used for the championship export in AdminView.
export const apiDownload = async (path, { token, fallbackFilename } = {}) => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  });

  if (handleUnauthorized(response)) {
    throw new ApiError('Session expired', 401, null);
  }
  if (!response.ok) {
    throw new ApiError(`Download failed (${response.status})`, response.status, null);
  }

  const disposition = response.headers.get('Content-Disposition');
  let filename = fallbackFilename || 'download';
  if (disposition) {
    const match = disposition.match(/filename="(.+)"/);
    if (match) filename = match[1];
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
};
